import { gql } from '@apollo/client';

/**
 * Every field here is readable by the `anonymous` role, so the logged-out profile
 * renders the same page the viewer would see. Adding a field the role can't read
 * fails the whole query rather than that one field.
 */
export const PLAYER_PROFILE_FIELDS = gql`
  fragment playerProfileFields on UserProfiles {
    id
    username
    fullName
    preferredName
    cityName
    gender
    aboutMe
    coachStatus
    profileImagePath
    coverImagePath
    instagramUrl
    facebookUrl
    tiktokUrl
    linkedinUrl
    xUrl
    birthdayMonth
    birthdayDay
    birthdayYear
    pickleballSkillLevel {
      id
      displayName
    }
    tennisSkillLevel {
      id
      displayName
    }
    padelSkillLevel {
      id
      displayName
    }
    sports {
      id
      sport
    }
    pickleballRatingScales {
      id
      rating
      scale {
        id
        shortName
      }
    }
    tennisRatingScales {
      id
      rating
      scale {
        id
        shortName
      }
    }
    padelRatingScales {
      id
      rating
      scale {
        id
        shortName
      }
    }
    playerFinderAvailability {
      dayOfWeek
      timeOfDay
    }
    venueFollows(where: { status: { _eq: ACTIVE } }) {
      id
      venue {
        id
        title
        addressString
        latitude
        longitude
        indoorCourtCount
        images(where: { isVisible: { _eq: true } }, orderBy: { isCover: DESC }, limit: 1) {
          id
          url
        }
      }
    }
    userFiles(where: { fileCategory: { _eq: PLAYER_GALLERY } }) {
      id
      url
      path
      providerUrl
      fileFormat
    }
    identityVerifications(where: { status: { _eq: VERIFIED } }, limit: 1) {
      status
    }
    followersAggregate(where: { status: { _eq: ACTIVE } }) {
      aggregate {
        count
      }
    }
    playerReviewsAggregate {
      aggregate {
        count
        avg {
          rating
        }
      }
    }
    playerReviews(orderBy: { createdAt: DESC }, limit: 10) {
      id
      rating
      review
      createdAt
      externalFullName
      reviewer {
        id
        username
        fullName
        preferredName
        profileImagePath
      }
    }
  }
`;

/** Anonymous viewers: no follow row to resolve. */
export const GET_PLAYER_PROFILE_PUBLIC = gql`
  query getPlayerProfilePublic($username: String!, $now: timestamptz!) {
    userProfiles(where: { username: { _eq: $username } }) {
      ...playerProfileFields
    }
    # "Games played": past player-listed games the player hosted or was accepted into.
    # Both are public (events + the accepted-seats view), so anonymous gets them too.
    hostedGamesAggregate: eventsAggregate(
      where: {
        hostUserProfile: { username: { _eq: $username } }
        isPlayerListed: { _eq: true }
        status: { _eq: PUBLISHED }
        deletedAt: { _isNull: true }
        startDateTime: { _lt: $now }
      }
    ) {
      aggregate {
        count
      }
    }
    joinedGamesAggregate: eventAcceptedPlayRequestsAggregate(
      where: {
        requesterProfile: { username: { _eq: $username } }
        event: {
          isPlayerListed: { _eq: true }
          status: { _eq: PUBLISHED }
          deletedAt: { _isNull: true }
          startDateTime: { _lt: $now }
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
  ${PLAYER_PROFILE_FIELDS}
`;

/** Adds the follow row and the two facts that gate "Write a review". */
export const GET_PLAYER_PROFILE = gql`
  query getPlayerProfile($username: String!, $viewerId: uuid!, $now: timestamptz!) {
    userProfiles(where: { username: { _eq: $username } }) {
      ...playerProfileFields
      viewerReview: playerReviews(where: { reviewerUserId: { _eq: $viewerId } }, limit: 1) {
        id
      }
    }
    userFollows(
      where: {
        followedProfile: { username: { _eq: $username } }
        followerUserId: { _eq: $viewerId }
      }
    ) {
      status
      followedUserId
      followerUserId
    }
    # Root level, not nested: play_requests has no reverse relationship from user_profiles.
    acceptedPlayRequests: playRequests(
      where: {
        status: { _eq: ACCEPTED }
        _and: [
          {
            _or: [{ requesterUserId: { _eq: $viewerId } }, { recipientUserId: { _eq: $viewerId } }]
          }
          {
            _or: [
              { requesterProfile: { username: { _eq: $username } } }
              { recipientProfile: { username: { _eq: $username } } }
            ]
          }
        ]
      }
      limit: 1
    ) {
      id
    }
    # "Games played": past player-listed games the player hosted or was accepted into.
    # Both are public (events + the accepted-seats view), so anonymous gets them too.
    hostedGamesAggregate: eventsAggregate(
      where: {
        hostUserProfile: { username: { _eq: $username } }
        isPlayerListed: { _eq: true }
        status: { _eq: PUBLISHED }
        deletedAt: { _isNull: true }
        startDateTime: { _lt: $now }
      }
    ) {
      aggregate {
        count
      }
    }
    joinedGamesAggregate: eventAcceptedPlayRequestsAggregate(
      where: {
        requesterProfile: { username: { _eq: $username } }
        event: {
          isPlayerListed: { _eq: true }
          status: { _eq: PUBLISHED }
          deletedAt: { _isNull: true }
          startDateTime: { _lt: $now }
        }
      }
    ) {
      aggregate {
        count
      }
    }
  }
  ${PLAYER_PROFILE_FIELDS}
`;
